import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export function CompanySlotPacks({ onPurchased }) {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(() =>
    api.get("/payments/company-slots/config").then(({ data }) => setCfg(data)).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);

  const buy = async (pack) => {
    setBusy(pack.quantity);
    try {
      const { data: order } = await api.post("/payments/company-slots/order", { quantity: pack.quantity });
      const finalize = async (payload) => {
        const { data } = await api.post("/payments/company-slots/verify", payload);
        toast.success(`Added ${data.slots_added} company slot${data.slots_added === 1 ? "" : "s"} to your account.`);
        await refresh();
        onPurchased?.(data);
      };
      if (order.mock_mode || !order.key_id) {
        await finalize({ order_id: order.order_id });
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay checkout."); return; }
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "JobBasket.Network",
        description: `${order.slots} Company Slot${order.slots === 1 ? "" : "s"}`,
        order_id: order.order_id,
        prefill: { email: user?.email, name: user?.name },
        theme: { color: "#f59e0b" },
        handler: async (res) => {
          try {
            await finalize({
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
            });
          } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        },
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (!cfg) {
    return <div className="py-4 flex justify-center" data-testid="slot-packs-loading"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  }
  const q = cfg.quota || {};
  const unlimited = q.remaining == null;
  return (
    <div data-testid="company-slot-packs">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-600">
          Company credits: <span className="font-semibold text-slate-900" data-testid="slot-packs-balance">{unlimited ? "Unlimited" : `${q.remaining} left`}</span>
          {!unlimited && (
            <span className="text-slate-400 text-xs ml-1">
              (lifetime · {q.companies_used ?? 0} of {q.cap} used{q.extra_slots ? ` · ${q.extra_slots} purchased` : ""})
            </span>
          )}
        </p>
        <p className="text-xs text-slate-500">₹{cfg.price_per_slot} per slot · never expire</p>
      </div>
      {unlimited ? (
        <p className="mt-3 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="slot-packs-unlimited">
          Your plan already includes unlimited company saves — no top-up needed.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-3 mt-3">
          {cfg.packs.map((p) => (
            <button
              key={p.quantity}
              data-testid={`slot-pack-buy-${p.quantity}`}
              onClick={() => buy(p)}
              disabled={busy !== null}
              className="rounded-xl border border-amber-200 bg-amber-50/60 hover:bg-amber-100/70 transition-colors px-3 py-3 text-center disabled:opacity-60"
            >
              {busy === p.quantity ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto text-amber-600" />
              ) : (
                <>
                  <p className="text-lg font-bold text-slate-900">{p.quantity}</p>
                  <p className="text-[11px] text-slate-500">{p.quantity === 1 ? "slot" : "slots"}</p>
                  <p className="text-sm font-semibold text-amber-700 mt-1">₹{p.amount}</p>
                </>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
